/**
 * sindhi-delivery-badge.js
 *
 * Extracted from snippets/sindhi-delivery-badge.liquid (CLAUDE.md perf fix, 2026-07-06).
 * That snippet is rendered once per product card (149x on the homepage); it previously
 * emitted this entire script inline on every render, guarded only by window.__sdpCdInit
 * so the body only ever ran once — but the browser still had to parse/discard the
 * duplicated markup on every single render. Moving it to a single external file loaded
 * once via layout/theme.liquid (deferred) removes that duplication entirely.
 *
 * Loaded with `defer` in layout/theme.liquid, next to sindhi-location-manager.js.
 * Behavior-preserving: the IIFE already guarded its own init() behind
 * `document.readyState === 'loading' ? addEventListener('DOMContentLoaded', init) : init()`,
 * so it already tolerated running either mid-parse (inline, previous behavior) or after
 * parse completes (deferred, this file) — deferred execution is strictly safer since all
 * product cards exist in the DOM by the time init() runs. The MutationObserver on
 * html[data-sdp-zone] additionally guards against the event-vs-listener race where
 * sindhi-location-manager.js's _hydrate() (also deferred) could set the zone attribute
 * before this script's own event listeners are attached — see comment below.
 *
 * ZONE-AWARE JS — IIFE-guarded, runs once per page.
 * Handles:
 *   1. PDP zone panels [data-sdp-zone-panel] — switches ETA label + countdown
 *   2. Card badges     [data-sdp-card-badge]  — upgrades India range → NCR today/tomorrow
 *   3. Live updates on sdp:location:set / sdp:location:clear events
 */
(function () {
  if (window.__sdpCdInit) return;
  window.__sdpCdInit = true;

  /* ── IST helpers ─────────────────────────────────────────────── */
  function fmtTime(secs) {
    secs = Math.max(0, secs);
    var h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    if (h > 0) return h + 'h ' + (m < 10 ? '0' : '') + m + 'm';
    if (m > 0) return m + 'm ' + (s < 10 ? '0' : '') + s + 's';
    return s + 's';
  }
  window.__sdpFmtTime = fmtTime;

  /* Money, using the shop's own currency format rather than a hardcoded symbol.
     COD amounts arrive as whole rupees (cod_min_order: 4000 means Rs 4,000), not
     paise, so every Shopify {{amount*}} placeholder collapses to the same
     grouped integer here. Falls back to the bare grouped number if the shop's
     money_format is unavailable. */
  function fmtMoney(amount, format) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    var grouped = n.toLocaleString('en-IN');
    if (!format) return grouped;
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/gi, grouped);
  }

  /* Fill %token% placeholders left in a server-translated string. The Liquid
     side renders e.g. `'…cod_available_at' | t: pin: '%pin%'`, so translators
     write natural copy and only the runtime value is substituted here.
     %…% rather than {…}: a literal brace inside a Liquid `{{ }}` output tag
     truncates the tag and breaks the whole data island. */
  function fill(tpl, values) {
    if (!tpl) return '';
    return Object.keys(values).reduce(function (out, k) {
      return out.split('%' + k + '%').join(values[k]);
    }, tpl);
  }

  /* ── Countdown timer per panel ──────────────────────────────── */
  var _timers = [];
  function stopTimer(panel) {
    for (var i = _timers.length - 1; i >= 0; i--) {
      if (_timers[i].panel === panel) {
        clearInterval(_timers[i].id);
        _timers.splice(i, 1);
      }
    }
    var timerEl = panel.querySelector('[data-sdp-countdown-timer]');
    if (timerEl) timerEl.textContent = '';
  }
  function startTimer(panel) {
    stopTimer(panel);
    var suffix = panel.dataset.countdownSuffix || '';
    var labelEl = panel.querySelector('[data-sdp-eta-label]');
    var timerEl = panel.querySelector('[data-sdp-countdown-timer]');
    if (!labelEl) return;
    var opts = {
      hasCutoff:       true,
      cutoffHour:      parseInt(panel.dataset.cutoffHour   || '0', 10),
      cutoffMinute:    parseInt(panel.dataset.cutoffMinute || '0', 10),
      dispatchToday:   true,
      todayLabel:      panel.dataset.today  || '',
      tomorrowLabel:   panel.dataset.tomorrow || '',
      ncrResolvedLabel:'',
      fallbackLabel:   ''
    };
    function tick() {
      var r = SindhiLocation.resolveEta(opts);
      labelEl.textContent = r.label;
      if (r.secsUntilCutoff !== null) {
        if (timerEl) timerEl.textContent = ' · ' + fmtTime(r.secsUntilCutoff) + ' ' + suffix;
      } else {
        if (timerEl) timerEl.textContent = '';
        stopTimer(panel);
      }
    }
    var id = setInterval(tick, 1000);
    _timers.push({ panel: panel, id: id });
    tick();
  }

  /* ── PDP zone panel update ───────────────────────────────────── */
  function updatePDPPanel(panel) {
    var zone       = document.documentElement.dataset.sdpZone || '';
    var hasCtf     = panel.dataset.hasCutoff === 'true';
    var dispToday  = panel.dataset.dispatchToday === 'true';
    var ncrOnly    = panel.dataset.ncrOnly    === 'true';
    var disrupted  = panel.dataset.disrupted  === 'true';
    var indiaRange = panel.dataset.indiaRange || '';
    var ncrEta     = panel.dataset.ncrEta     || '';
    var labelEl    = panel.querySelector('[data-sdp-eta-label]');
    var ncrOnlyRow = panel.querySelector('[data-sdp-ncr-only-row]');
    var cutoffRow  = panel.querySelector('.sdp-delivery__row--cutoff');

    stopTimer(panel);

    if (!zone) {
      /* No PIN — CSS handles visibility; nothing more to do in JS */
      return;
    }

    /* Cutoff-note visibility is a two-way switch, set once here before branching.
       The India branch used to hide it and nothing ever un-hid it, so a shopper
       who arrived with a non-NCR PIN and then switched to an NCR one lost the
       "Order before 12:00 PM IST for same-day delivery in Delhi-NCR" line — the
       one row that makes the same-day promise actionable, gone for exactly the
       audience it is written for (found live 2026-09-15, 400001 -> 110001). */
    if (cutoffRow) cutoffRow.hidden = (zone !== 'ncr');

    if (zone === 'ncr') {
      if (disrupted) return; /* disruption msg already correct in zone-content */
      if (ncrOnly && ncrOnlyRow) ncrOnlyRow.hidden = false;
      if (labelEl) {
        /* Only run the today→tomorrow cutoff countdown when the store actually
           dispatches today. On a weekly-off day / holiday the server already
           resolved ncrEta to the next working day — show that, no countdown. */
        if (hasCtf && dispToday) {
          startTimer(panel);
        } else if (ncrEta) {
          labelEl.textContent = ncrEta;
        }
      }
    } else {
      /* India zone (or any non-ncr zone) — cutoff row already hidden above */
      if (ncrOnly && zone !== 'ncr') {
        /* Product only delivers to NCR — show unavailable for non-NCR */
        /* Translated server-side (CLAUDE.md §1) — never a literal here. */
        if (labelEl) labelEl.textContent = panel.dataset.ncrOnlyMsg || '';
        if (ncrOnlyRow) ncrOnlyRow.hidden = false;
      } else {
        if (labelEl && indiaRange) labelEl.textContent = indiaRange;
        /* The NCR-only note is for NCR shoppers of an NCR-only product. */
        if (ncrOnlyRow) ncrOnlyRow.hidden = !(ncrOnly && zone === 'ncr');
      }
    }
  }

  /* ── Card badge update ───────────────────────────────────────── */
  function updateCardBadges() {
    var zone = document.documentElement.dataset.sdpZone || '';
    var loc  = window.SindhiLocation ? SindhiLocation.get() : null;
    /* Only target delivery-badge pills (have data-india-range); skip CSS-only pills like sdp-today-pill */
    document.querySelectorAll('[data-sdp-card-badge][data-india-range]').forEach(function (badge) {
      var hasCtf     = badge.dataset.hasCutoff === 'true';
      var dispToday  = badge.dataset.dispatchToday === 'true';
      var indiaRange = badge.dataset.indiaRange || '';
      var ncrEta     = badge.dataset.ncrEta || '';
      var today      = badge.dataset.today  || '';
      var tmrw       = badge.dataset.tomorrow || '';
      var ch         = parseInt(badge.dataset.cutoffHour   || '0', 10);
      var cm         = parseInt(badge.dataset.cutoffMinute || '0', 10);

      if (zone === 'ncr') {
        var r = SindhiLocation.resolveEta({
          hasCutoff: hasCtf, cutoffHour: ch, cutoffMinute: cm, dispatchToday: dispToday,
          todayLabel: today, tomorrowLabel: tmrw, ncrResolvedLabel: ncrEta,
          fallbackLabel: (loc && loc.eta_label) ? loc.eta_label : (today || indiaRange)
        });
        badge.textContent = r.label;
        /* Only style actual pills. The timeline's "Delivered" date span opts into
           this same contract to stay zone-aware (see sindhi-delivery-badge.liquid),
           and .sdp-pill--today paints a forest-tinted pill background — correct on
           a card badge, wrong on a timeline date. */
        if (badge.classList.contains('sdp-pill')) {
          badge.classList.toggle('sdp-pill--today', !!(r.label && r.label !== indiaRange));
        }
      } else {
        if (indiaRange) badge.textContent = indiaRange;
        if (badge.classList.contains('sdp-pill')) badge.classList.remove('sdp-pill--today');
      }
    });
  }

  /* "Get it Today" pills are CSS-only server output; on a cached page one outlives
     the cutoff. Hide it once IST time-of-day passes its data-cutoff-sec. */
  function hideExpiredTodayPills() {
    if (typeof _getISTSecondsNow !== 'function') return;
    var now = _getISTSecondsNow();
    /* Inline display, not [hidden]: .sdp-today-pill sets display:inline-flex in
       sindhi-brand-global.css, which beats the UA [hidden] rule. Hide-only: once a
       pill has expired it must never come back — after IST midnight `now` resets
       to ~0 and a re-show would revive yesterday's "Get it Today" on a stale page. */
    document.querySelectorAll('.sdp-today-pill[data-cutoff-sec]').forEach(function (p) {
      if (now >= parseInt(p.dataset.cutoffSec, 10)) p.style.display = 'none';
    });
  }
  setInterval(hideExpiredTodayPills, 30000);

  /* ── Cart ETA update (for cart drawer delivery spans) ────────── */
  var _cartCountdownTimers = [];
  function stopCartCountdowns() {
    _cartCountdownTimers.forEach(function (t) { clearInterval(t); });
    _cartCountdownTimers = [];
  }

  function updateCartEta() {
    var zone = document.documentElement.dataset.sdpZone || '';
    var loc  = window.SindhiLocation ? SindhiLocation.get() : null;
    stopCartCountdowns();
    document.querySelectorAll('[data-sdp-cart-eta]').forEach(function (span) {
      var textEl = span.querySelector('[data-sdp-cart-eta-text]');
      var cdEl   = span.querySelector('[data-sdp-cart-countdown]');
      if (!textEl) return;
      var hasCtf     = span.dataset.hasCutoff === 'true';
      var dispToday  = span.dataset.dispatchToday === 'true';
      var indiaRange = span.dataset.indiaRange || '';
      var ncrEta     = span.dataset.ncrEta || '';
      var today      = span.dataset.today || '';
      var tmrw       = span.dataset.tomorrow || '';
      var ch         = parseInt(span.dataset.cutoffHour || '0', 10);
      var cm         = parseInt(span.dataset.cutoffMinute || '0', 10);
      var suffix     = span.dataset.countdownSuffix || '';
      var opts = {
        hasCutoff: hasCtf, cutoffHour: ch, cutoffMinute: cm, dispatchToday: dispToday,
        todayLabel: today, tomorrowLabel: tmrw, ncrResolvedLabel: ncrEta,
        fallbackLabel: (loc && loc.eta_label) ? loc.eta_label : (today || indiaRange)
      };
      function tick() {
        if (zone !== 'ncr') {
          if (indiaRange) { textEl.textContent = indiaRange; span.setAttribute('aria-label', indiaRange); }
          if (cdEl) cdEl.textContent = '';
          return;
        }
        var r = SindhiLocation.resolveEta(opts);
        if (r.label) { textEl.textContent = r.label; span.setAttribute('aria-label', r.label); }
        if (cdEl) {
          cdEl.textContent = (r.secsUntilCutoff !== null) ? (' · ' + fmtTime(r.secsUntilCutoff) + ' ' + suffix) : '';
        }
      }
      tick();
      if (cdEl && zone === 'ncr') {
        var id = setInterval(tick, 1000);
        _cartCountdownTimers.push(id);
      }
    });
  }

  /* ── COD config island — never cache null so late-parsed DOM is retried ── */
  function getCodConfig() {
    if (window.__sdpCodConfig) return window.__sdpCodConfig;
    var el = document.getElementById('sdp-cod-config');
    if (!el) return null;
    try {
      var cfg = JSON.parse(el.textContent);
      if (cfg && cfg.enabled) window.__sdpCodConfig = cfg;
      return cfg;
    } catch(e) { return null; }
  }

  /* ── PDP COD row update ─────────────────────────────────────────── */
  function updateCodRow(panel) {
    var codRow = panel.querySelector('[data-sdp-cod-row]');
    if (!codRow) return;
    var codLabel = codRow.querySelector('[data-sdp-cod-label]');
    if (!codLabel) return;
    var cfg = getCodConfig();
    if (!cfg || !cfg.enabled) {
      codRow.classList.add('sdp-cod-row--hidden');
      return;
    }
    var loc = window.SindhiLocation ? SindhiLocation.get() : null;
    /* html.dataset.sdpPin is set synchronously by _hydrate() (sindhi-location-manager.js)
       — use it as fallback so COD row works even when SindhiLocation.get() hasn't resolved yet */
    var pin = (loc && String(loc.pin || '')) || document.documentElement.dataset.sdpPin || '';
    if (!pin) {
      if (cfg.precheck_msg) {
        codLabel.textContent = cfg.precheck_msg;
        codRow.classList.remove('sdp-cod-row--hidden');
        codRow.classList.remove('sdp-delivery__row--cod--available','sdp-delivery__row--cod--unavailable');
      } else {
        codRow.classList.add('sdp-cod-row--hidden');
      }
      return;
    }
    var rangeEl = codRow.querySelector('[data-sdp-cod-range]');
    /* loc may be null when SindhiLocation.get() TTL-expires while html[data-sdp-pin]
       persists via the TTL-less inline hydrator in theme.liquid. Never dereference loc
       unguarded. loc.cod_eligible is only set by the pincode-check widget path; fall
       through to the metaobject pincodes whitelist for the location-bar / auto-fetch path. */
    var isEligible = !!(loc && loc.cod_eligible === true);
    if (!isEligible && cfg.pincodes && cfg.pincodes.length) {
      /* trim: a CRLF-stored whitelist leaves a trailing \r on each entry, which an
         exact match would never equal (Liquid can't strip it — no \r escape). */
      isEligible = cfg.pincodes.some(function (p) { return String(p).trim() === String(pin); });
    }
    if (isEligible) {
      codRow.classList.add('sdp-delivery__row--cod--available');
      codRow.classList.remove('sdp-delivery__row--cod--unavailable');
      /* All copy is translated server-side (CLAUDE.md §1); only the pin and the
         amount are runtime. The surcharge really can differ from the metaobject
         value — the Shiprocket proxy returns a per-pincode figure — so it is
         formatted here rather than baked into the island. */
      var i18n = cfg.i18n || {};
      var lbl = fill(i18n.available_at, { pin: pin });
      var surcharge = (loc && loc.cod_surcharge > 0) ? loc.cod_surcharge : (cfg.surcharge || 0);
      if (surcharge > 0) {
        lbl += ' · ' + (cfg.surcharge_label ||
                        fill(i18n.handling_fee, { amount: fmtMoney(surcharge, cfg.money_format) }));
      }
      codLabel.textContent = lbl;
      if (rangeEl) {
        var rangeText = '';
        var hasMin = cfg.min_order !== null && cfg.min_order !== undefined;
        /* Suppress sentinel max (≥ 99,99,999) — only show meaningful upper bound */
        var hasMax = cfg.max_order !== null && cfg.max_order !== undefined && cfg.max_order < 9999999;
        var minStr = fmtMoney(cfg.min_order, cfg.money_format);
        var maxStr = fmtMoney(cfg.max_order, cfg.money_format);
        if (hasMin && hasMax)      rangeText = fill(i18n.order_range, { min: minStr, max: maxStr });
        else if (hasMin)           rangeText = fill(i18n.min_order,   { amount: minStr });
        else if (hasMax)           rangeText = fill(i18n.max_order,   { amount: maxStr });
        if (rangeText) { rangeEl.textContent = rangeText; rangeEl.hidden = false; }
        else rangeEl.hidden = true;
      }
      codRow.classList.remove('sdp-cod-row--hidden');
    } else {
      codRow.classList.remove('sdp-delivery__row--cod--available');
      if (rangeEl) rangeEl.hidden = true;
      if (cfg.unavailable_msg) {
        codRow.classList.add('sdp-delivery__row--cod--unavailable');
        codLabel.textContent = cfg.unavailable_msg;
        codRow.classList.remove('sdp-cod-row--hidden');
      } else {
        codRow.classList.add('sdp-cod-row--hidden');
      }
    }
  }

  /* ── Cart drawer COD badge update ───────────────────────────────── */
  function updateCartCodBadge() {
    var badges = document.querySelectorAll('[data-sc-cod-badge]');
    if (!badges.length) return;
    var loc = window.SindhiLocation ? SindhiLocation.get() : null;
    var show = !!(loc && loc.cod_eligible === true);
    /* Fallback to the metaobject pincode whitelist (sdp-cod-config island) —
       mirrors updateCodRow()'s fallback so the cart badge and PDP row never
       disagree when loc.cod_eligible hasn't resolved yet (e.g. TTL-expired
       localStorage record but html[data-sdp-pin] still hydrated). */
    if (!show) {
      var cfg = getCodConfig();
      var pin = (loc && String(loc.pin || '')) || document.documentElement.dataset.sdpPin || '';
      if (cfg && cfg.enabled && pin && cfg.pincodes &&
          cfg.pincodes.some(function (p) { return String(p).trim() === String(pin); })) {
        show = true;
      }
    }
    badges.forEach(function(b) { b.hidden = !show; });
  }

  /* ── Init & event listeners ──────────────────────────────────── */
  function init() {
    document.querySelectorAll('[data-sdp-zone-panel]').forEach(updatePDPPanel);
    updateCardBadges();
    hideExpiredTodayPills();
    updateCartEta();
    document.querySelectorAll('[data-sdp-zone-panel]').forEach(updateCodRow);
    updateCartCodBadge();
    /* Re-run after cart re-renders (item added/removed).
       Only react to new ELEMENT nodes — our own textContent writes add TEXT nodes (type 3),
       which are ignored, preventing an infinite update loop. */
    var _cartEtaPending = false;
    var cartDrawer = document.querySelector('.sc-drawer');
    if (cartDrawer && window.MutationObserver) {
      new MutationObserver(function (mutations) {
        if (_cartEtaPending) return;
        var hasNewEl = mutations.some(function (m) {
          return Array.prototype.some.call(m.addedNodes, function (n) { return n.nodeType === 1; });
        });
        if (!hasNewEl) return;
        _cartEtaPending = true;
        setTimeout(function () { _cartEtaPending = false; updateCartEta(); updateCartCodBadge(); }, 50);
      }).observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  ['sdp:location:set', 'sdp:location:clear', 'sdp:location:ready'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      document.querySelectorAll('[data-sdp-zone-panel]').forEach(updatePDPPanel);
      updateCardBadges();
      updateCartEta();
      document.querySelectorAll('[data-sdp-zone-panel]').forEach(updateCodRow);
      updateCartCodBadge();
    });
  });

  /* MutationObserver: react to _hydrate() calls from sindhi-location-manager.js.
     _hydrate() sets html[data-sdp-zone] synchronously — this fires even when
     sdp:location:ready has already been dispatched before our listeners registered,
     or when the deferred script runs before our DOMContentLoaded handler. */
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'data-sdp-zone') {
          document.querySelectorAll('[data-sdp-zone-panel]').forEach(updatePDPPanel);
          document.querySelectorAll('[data-sdp-zone-panel]').forEach(updateCodRow);
          updateCardBadges();
          updateCartEta();
          updateCartCodBadge();
          break;
        }
      }
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-sdp-zone'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
